import {Component, OnInit} from '@angular/core';
import {ProjectViewModel} from '../user/garage/project.view-model';
import {RankingCollectionViewModel, RankingDataProviderService} from "./ranking.data-provider.service";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractComponent} from "../../core/components/abstract-component";
import {VehicleTypeEnum} from "../../core/enums/vehicle-type.enum";
import {SeoService} from "../../core/services/seo.service";
import {environment} from "../../../environments/environment";
import {TokenDataProviderService} from "../../core/services/token-data-provider.service";
import {CacheRefreshService} from "../../core/services/cache-refresh.service";
import {PullToRefreshDirective} from "../../shared/directives/pull-to-refresh.directive";
import {RankingCategory} from './ranking-category.type';
import {RankingUserViewModel} from './ranking-user.view-model';
import {TabViewChangeEvent} from 'primeng/tabview';
import {Location} from '@angular/common';
import {map} from 'rxjs/operators';
import {ProjectPerformanceMetricsService} from '../../core/services/project-performance-metrics.service';
import {ProjectPerformanceMetrics} from './project-performance-metrics.interface';
import {QueryParamsApiModel} from '../../api/models/query-params-api.model';
import {Observable} from 'rxjs';
import {PublicResourceUrlService} from "../../shared/services/public-resource-url.service";
import {QrDialogData} from "../../shared/qr/qr-dialog.component";

interface RankingTabState {
  category: RankingCategory;
  page: number;
  loadedPage: number;
  total: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  loaded: boolean;
  error: boolean;
  projects: ProjectViewModel[];
  users: RankingUserViewModel[];
  strongestSort: 'score';
}

@Component({
  selector: 'app-ranking',
  templateUrl: './ranking.component.html',
  styleUrls: ['./ranking.component.scss']
})
export class RankingComponent extends AbstractComponent implements OnInit {
  public readonly pageSize = 100;
  public readonly categories: Array<{label: string; key: RankingCategory}> = [
    {label: 'features.ranking.tabs.overall', key: 'overall'},
    {label: 'features.ranking.tabs.car', key: 'car'},
    {label: 'features.ranking.tabs.motorcycle', key: 'motorcycle'},
    {label: 'features.ranking.tabs.quad', key: 'quad'},
    {label: 'features.ranking.tabs.users', key: 'users'},
    {label: 'features.ranking.tabs.strongest', key: 'strongest'},
  ];
  public activeCategory: RankingCategory = 'overall';
  public activeTabIndex = 0;
  public tabStates: Record<RankingCategory, RankingTabState> = this.createInitialTabStates();
  public qrDialogVisible = false;
  public qrDialogData: QrDialogData | null = null;

  constructor(
    private readonly rankingDPS: RankingDataProviderService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly seo: SeoService,
    private readonly tokenDataProvider: TokenDataProviderService,
    private readonly cacheRefresh: CacheRefreshService,
    private readonly performanceMetrics: ProjectPerformanceMetricsService,
    private readonly publicResourceUrlService: PublicResourceUrlService
  ) {
    super();
  }

  ngOnInit(): void {
    const title = this.translate.instant('common.ranking');
    const description = this.translate.instant('meta.ranking_description') === 'meta.ranking_description'
      ? 'Top rated garages and projects on Watch This Garage.'
      : this.translate.instant('meta.ranking_description');
    const url = `${environment.appUrl}/ranking`;
    this.seo.setTitle(title);
    this.seo.setDescription(description);
    this.seo.setOpenGraph({title, description, url, type: 'website'});
    this.seo.setCanonical(url);
    this.seo.setJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "description": description,
      "url": url
    });

    this.applyQueryState(
      (this.route.snapshot.queryParams?.['tab'] as RankingCategory | undefined) || 'overall',
      Number(this.route.snapshot.queryParams?.['page'] || 1)
    );
    this.subscriptions.push(this.route.queryParamMap.pipe(
      map((params) => ({
        tab: (params.get('tab') as RankingCategory | null) || 'overall',
        page: Number(params.get('page') || 1),
      }))
    ).subscribe(({tab, page}) => this.applyQueryState(tab, page)));
  }

  public onTabChange(event: TabViewChangeEvent): void {
    const category = this.categories[event.index]?.key || 'overall';
    this.activeTabIndex = event.index;
    this.activeCategory = category;
    this.syncQueryParams(category, this.getTabState(category).page || 1);
    this.loadCategory(category);
  }

  public onPageChange(event: {page: number; rows: number}): void {
    const nextPage = (event.page || 0) + 1;
    const state = this.getTabState(this.activeCategory);
    state.page = nextPage;
    state.limit = event.rows || this.pageSize;
    this.syncQueryParams(this.activeCategory, nextPage);
    this.loadCategory(this.activeCategory, true);
  }

  onPullToRefresh(pullToRefresh: PullToRefreshDirective): void {
    this.cacheRefresh.requestRefresh();
    this.loadCategory(this.activeCategory, true, () => pullToRefresh.complete());
  }

  public get activeState(): RankingTabState {
    return this.getTabState(this.activeCategory);
  }

  public get hasProjects(): boolean {
    return this.activeState.projects?.some((project) => !project?.isPlaceholder) ?? false;
  }

  public get hasUsers(): boolean {
    return (this.activeState.users?.length ?? 0) > 0;
  }

  public isProjectTab(category: RankingCategory): boolean {
    return category !== 'users';
  }

  public getProjectRank(index: number, category: RankingCategory = this.activeCategory): number {
    const state = this.getTabState(category);
    return ((state.page || 1) - 1) * (state.limit || this.pageSize) + index + 1;
  }

  public getStrongestMetrics(project: ProjectViewModel): ProjectPerformanceMetrics {
    return this.performanceMetrics.getEffectiveMetrics(project);
  }

  public getDefaultImageType(project: ProjectViewModel): 'car' | 'motorcycle' {
    return project.vehicleType?.includes(VehicleTypeEnum.CAR) ? 'car' : 'motorcycle';
  }

  getIconForStar(star: number, value: number): string {
    const fullStarIcon = 'fa-solid fa-star';
    const halfStarIcon = 'fa-solid fa-star-half-stroke';
    let icon = 'fa-regular fa-star';

    if (value >= star) {
      icon = fullStarIcon;
    } else if (value > (star - 1) && value < star) {
      icon = halfStarIcon;
    }

    return icon;
  }

  public openQrDialog(projectVM: ProjectViewModel, event?: Event): void {
    if (!this.ensureLoggedIn(event)) {
      return;
    }

    this.qrDialogData = {
      resourceType: 'project',
      title: projectVM.vehicle?.fullVehicleIdentity || projectVM.alias || '',
      subtitle: projectVM.shortDesc || null,
      url: this.getProjectUrl(projectVM)
    };
    this.qrDialogVisible = true;
  }

  getProjectUrl(projectVM: ProjectViewModel): string {
    if (!projectVM?.slug) {
      return '';
    }

    return this.publicResourceUrlService.getProjectUrl(projectVM.user?.slug || null, projectVM.slug);
  }

  openGarage(projectVM: ProjectViewModel, event?: Event): void {
    if (!this.ensureLoggedIn(event) || !projectVM.user?.slug) {
      return;
    }
    this.router.navigate(['/', projectVM.user.slug, 'garage']);
  }

  openProject(projectVM: ProjectViewModel, event?: Event): void {
    if (!this.ensureLoggedIn(event) || !projectVM?.slug) {
      return;
    }
    this.router.navigate(['/garage', projectVM.slug]);
  }

  openProfileForProject(projectVM: ProjectViewModel, event?: Event): void {
    if (!this.ensureLoggedIn(event) || !projectVM.user?.slug) {
      return;
    }
    this.router.navigate(['/', projectVM.user.slug, 'profile']);
  }

  openProfile(user: RankingUserViewModel, event?: Event): void {
    if (!this.ensureLoggedIn(event) || !user.user?.slug) {
      return;
    }
    this.router.navigate(['/', user.user.slug, 'profile']);
  }

  private applyQueryState(tab: RankingCategory, page: number): void {
    const category = this.categories.some((item) => item.key === tab) ? tab : 'overall';
    const normalizedPage = Number.isFinite(page) && page > 0 ? page : 1;
    this.activeCategory = category;
    this.activeTabIndex = this.categories.findIndex((item) => item.key === category);
    this.getTabState(category).page = normalizedPage;
    this.loadCategory(category);
  }

  private loadCategory(category: RankingCategory, force = false, onComplete?: () => void): void {
    const state = this.getTabState(category);
    if (state.loading) {
      return;
    }
    if (state.loaded && state.loadedPage === state.page && !force) {
      onComplete?.();
      return;
    }

    state.loading = true;
    state.error = false;
    if (category !== 'users') {
      state.projects = this.createProjectPlaceholders();
    }

    const queryParams = new QueryParamsApiModel();
    queryParams.page = state.page || 1;
    queryParams.limit = this.pageSize;

    const request$: Observable<RankingCollectionViewModel<any>> = category === 'users'
      ? this.rankingDPS.fetchUserRanking(queryParams)
      : this.rankingDPS.fetchProjectRanking(category as Exclude<RankingCategory, 'users'>, queryParams);

    this.subscriptions.push(request$.subscribe({
      next: (result: RankingCollectionViewModel<ProjectViewModel | RankingUserViewModel>) => {
        state.page = result.page || queryParams.page || 1;
        state.loadedPage = state.page;
        state.limit = result.limit || this.pageSize;
        state.total = result.total;
        state.totalPages = result.totalPages || 1;
        state.loaded = true;
        state.loading = false;

        if (category === 'users') {
          state.users = result.records as RankingUserViewModel[];
          state.projects = [];
        } else {
          state.projects = result.records as ProjectViewModel[];
          state.users = [];
        }

        onComplete?.();
      },
      error: () => {
        state.loading = false;
        state.error = true;
        state.projects = [];
        state.users = [];
        onComplete?.();
      }
    }));
  }

  private getTabState(category: RankingCategory): RankingTabState {
    return this.tabStates[category];
  }

  private createInitialTabStates(): Record<RankingCategory, RankingTabState> {
    return {
      overall: this.createTabState('overall'),
      car: this.createTabState('car'),
      motorcycle: this.createTabState('motorcycle'),
      quad: this.createTabState('quad'),
      users: this.createTabState('users'),
      strongest: this.createTabState('strongest'),
    };
  }

  private createTabState(category: RankingCategory): RankingTabState {
    return {
      category,
      page: 1,
      loadedPage: 0,
      total: 0,
      limit: this.pageSize,
      totalPages: 1,
      loading: false,
      loaded: false,
      error: false,
      projects: category === 'users' ? [] : this.createProjectPlaceholders(),
      users: [],
      strongestSort: 'score',
    };
  }

  private createProjectPlaceholders(): ProjectViewModel[] {
    return Array.from({length: 9}, () => {
      const projectVM = new ProjectViewModel();
      projectVM.isPlaceholder = true;
      return projectVM;
    });
  }

  private syncQueryParams(tab: RankingCategory, page: number): void {
    const urlTree = this.router.createUrlTree([], {
      queryParams: {tab, page},
      queryParamsHandling: 'merge',
      relativeTo: this.route
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  private ensureLoggedIn(event?: Event): boolean {
    if (this.tokenDataProvider.isLoggedIn()) {
      return true;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.router.navigate(['/login']);
    return false;
  }

  protected readonly VehicleTypeEnum = VehicleTypeEnum;
}
